import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Eye, Play, Users, Calendar, MapPin, Star, Award } from 'lucide-react';

interface ProjectCardProps {
  project: {
    id: string;
    title: string;
    logline?: string;
    description?: string;
    format?: string;
    director?: string;
    writer?: string;
    genre?: string;
    status?: string;
    production_status?: string;
    platform_metrics?: {
      total_followers?: number;
      total_views?: number;
      total_subscribers?: number;
    };
    demographics?: {
      age_range?: string;
      gender_split?: string;
      top_locations?: string[];
    };
    hero_image?: string;
    hero_image_url?: string;
    created_at?: string;
    updated_at?: string;
    slots?: any[];
    team?: Array<{
      name: string;
      role: string;
    }>;
    awards?: string[];
    tags?: string[];
  };
  type: 'anchor' | 'fill';
  onViewDetails?: (projectId: string) => void;
}

export const ProjectCard: React.FC<ProjectCardProps> = ({ 
  project, 
  type, 
  onViewDetails 
}) => {
  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(0)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(0)}K`;
    }
    return num.toString();
  };

  const getTotalReach = () => {
    // Try platform_metrics first
    if (project.platform_metrics) {
      const { total_followers = 0, total_views = 0, total_subscribers = 0 } = project.platform_metrics;
      const platformTotal = total_followers + total_views + total_subscribers;
      if (platformTotal > 0) return platformTotal;
    }
    
    // Try demographics as fallback
    if (project.demographics) {
      const demographics = project.demographics as any;
      const demographicsTotal = (demographics.total_followers || 0) + (demographics.total_views || 0) + (demographics.total_subscribers || 0);
      if (demographicsTotal > 0) return demographicsTotal;
    }
    
    // Check if there are any other reach-related fields in the project data
    const projectAny = project as any;
    if (projectAny.total_reach && projectAny.total_reach > 0) {
      return projectAny.total_reach;
    }
    
    if (projectAny.reach && projectAny.reach > 0) {
      return projectAny.reach;
    }
    
    if (projectAny.estimated_reach && projectAny.estimated_reach > 0) {
      return projectAny.estimated_reach;
    }
    
    // Return 0 as default value when no data available
    return 0;
  };

  const getSlotCounts = () => {
    const slots = project.slots || [];
    const narrativeSlots = slots.filter(slot => slot.slot_type === 'narrative_integration').length;
    const productSlots = slots.filter(slot => slot.slot_type === 'product_placement').length;
    return { narrative: narrativeSlots, product: productSlots, total: slots.length };
  };

  // Generate a placeholder image based on project title with proper text wrapping
  const getProjectImage = (title: string, isAnchor: boolean = true) => {
    const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];
    const colorIndex = title.length % colors.length;
    
    // Use different dimensions for anchor vs fill projects - maintain aspect ratio for anchor
    const width = isAnchor ? 400 : 80;
    const height = isAnchor ? 240 : 80; // 5:3 aspect ratio for anchor projects
    
    // Calculate appropriate font size based on title length and container size
    const maxCharsPerLine = isAnchor ? 18 : 8;
    const baseFontSize = isAnchor ? 22 : 10;
    const fontSize = title.length > maxCharsPerLine ?
      Math.max(baseFontSize * 0.8, isAnchor ? 16 : 8) : baseFontSize;
    
    // Split title into words and create lines that fit
    const words = title.split(' ');
    const lines = [];
    let currentLine = '';
    
    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      if (testLine.length <= maxCharsPerLine) {
        currentLine = testLine;
      } else {
        if (currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          // Word is too long, truncate it
          lines.push(word.substring(0, maxCharsPerLine - 3) + '...');
          currentLine = '';
        }
      }
    }
    if (currentLine) {
      lines.push(currentLine);
    }
    
    // Limit to maximum lines that fit
    const maxLines = isAnchor ? 3 : 2;
    const finalLines = lines.slice(0, maxLines);
    if (lines.length > maxLines) {
      finalLines[maxLines - 1] = finalLines[maxLines - 1].substring(0, maxCharsPerLine - 3) + '...';
    }
    
    // Calculate line height and starting Y position
    const lineHeight = fontSize * 1.3;
    const totalTextHeight = finalLines.length * lineHeight;
    const startY = (height - totalTextHeight) / 2 + fontSize * 0.75;
    
    // Generate text elements for each line
    const textElements = finalLines.map((line, index) =>
      `<text x="50%" y="${startY + (index * lineHeight)}" text-anchor="middle" fill="white" font-family="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" font-size="${fontSize}" font-weight="600">${line}</text>`
    ).join('');
    
    return `data:image/svg+xml,${encodeURIComponent(`
      <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" preserveAspectRatio="xMidYMid slice">
        <rect width="100%" height="100%" fill="${colors[colorIndex]}"/>
        ${textElements}
      </svg>
    `)}`;
  };

  // Get project image: check for uploaded assets first, then generate sample image
  const getImageSrc = () => {
    return project.hero_image || project.hero_image_url || getProjectImage(project.title, type === 'anchor');
  };

  // Get creator roles from team data or fallback to director/writer
  const getCreatorInfo = () => {
    if (project.team && project.team.length > 0) {
      return project.team.slice(0, 2); // Show first 2 team members
    }
    
    const creators = [];
    if (project.director) {
      creators.push({ name: project.director, role: 'Director' });
    }
    if (project.writer && project.writer !== project.director) {
      creators.push({ name: project.writer, role: 'Writer' });
    }
    
    return creators.length > 0 ? creators : [{ name: 'Unknown Creator', role: 'Creator' }];
  };

  // Get project tags from genre and tags
  const getProjectTags = () => {
    const tags = [];
    if (project.genre) {
      tags.push(project.genre);
    }
    if (project.tags && project.tags.length > 0) {
      tags.push(...project.tags.slice(0, 2)); // Add up to 2 additional tags
    }
    
    // If no tags available, return empty array (no default tags)
    return tags.slice(0, 3); // Max 3 tags total
  };

  // Get project awards
  const getProjectAwards = () => {
    return project.awards || [];
  };

  const slotCounts = getSlotCounts();
  const totalReach = getTotalReach();
  const creators = getCreatorInfo();
  const tags = getProjectTags();
  const awards = getProjectAwards();

  if (type === 'anchor') {
    return (
      <Card className="border border-neutral-200 rounded-xl overflow-hidden bg-white shadow-sm">
        <CardContent className="p-0">
          <div className="flex h-full">
            {/* Hero Image */}
            <div className="flex-shrink-0">
              <div className="w-[400px] h-full bg-neutral-100 overflow-hidden">
                <img
                  src={getImageSrc()}
                  alt={project.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Project Details */}
            <div className="flex-1 p-8 flex flex-col justify-between">
              <div className="mb-6">
                <h3 className="text-3xl font-bold text-neutral-900 mb-2">{project.title}</h3>
                <p className="text-neutral-600 mb-4">
                  {project.format || 'Episodic Series'} • {slotCounts.total} episodes
                </p>
                
                <p className="text-neutral-700 mb-6 leading-relaxed">
                  {project.logline || project.description || `A compelling project that showcases innovative storytelling and creative excellence.`}
                </p>

                {/* Quote - show for anchor projects with dynamic content */}
                <blockquote className="text-neutral-900 font-medium mb-6 italic">
                  "Matches your brief for authentic {project.genre ? `${project.genre.toLowerCase()} ` : ''}storytelling{tags.length > 0 ? ` with ${tags[0].toLowerCase()} tone` : ''}."
                </blockquote>

                {/* Tags - only show if we have tags */}
                {tags.length > 0 && (
                  <div className="flex gap-2 mb-6">
                    {tags.map((tag, index) => (
                      <Badge key={index} variant="secondary" className="bg-neutral-100 text-neutral-700 border-neutral-200 rounded-full px-3 py-1">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Creators */}
                <div className="flex items-center gap-8 mb-6">
                  {creators.slice(0, 2).map((creator, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <span className="font-medium text-neutral-900">{creator.name}</span>
                      {awards.length > index && (
                        <Badge className="bg-orange-100 text-orange-800 border-orange-200 text-xs flex items-center gap-1">
                          <Award className="w-3 h-3" />
                          {awards[index]}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Bottom Stats */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-6 text-sm text-neutral-600">
                  <div className="flex items-center gap-1">
                    <span className="font-semibold text-neutral-900">{slotCounts.total} Placements</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span>{slotCounts.narrative} Narrative</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span>{slotCounts.product} Product</span>
                  </div>
                </div>
                
                <div className="text-right">
                  <div className="text-lg font-semibold text-neutral-900">
                    Est. Views: {formatNumber(totalReach)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Fill projects keep the original compact layout
  return (
    <Card className="group hover:border-neutral-900 transition-all duration-300 border-neutral-200 overflow-hidden bg-white shadow-sm hover:shadow-md">
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          {/* Project Image */}
          <div className="flex-shrink-0">
            <div className="w-20 h-20 bg-neutral-100 rounded-lg overflow-hidden">
              <img 
                src={getImageSrc()} 
                alt={project.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Project Details */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-neutral-900 text-lg leading-tight mb-1 truncate">
                  {project.title}
                </h3>
                {project.logline && (
                  <p className="text-sm text-neutral-600 line-clamp-2 mb-2">
                    {project.logline}
                  </p>
                )}
              </div>
              <Badge className="bg-green-100 text-green-800 border-green-200" variant="outline">
                Fill
              </Badge>
            </div>

            {/* Creator Info */}
            {creators.length > 0 && creators[0].name !== 'Unknown Creator' && (
              <div className="flex flex-wrap gap-4 text-sm text-neutral-600 mb-3">
                <div className="flex items-center gap-1">
                  <span className="font-medium">{creators[0].role}:</span>
                  <span>{creators[0].name}</span>
                </div>
              </div>
            )}

            {/* Metrics Row */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 text-sm text-neutral-600">
                <div className="flex items-center gap-1">
                  <Eye className="w-4 h-4" />
                  <span className="font-medium">{formatNumber(getTotalReach())}</span>
                  <span>reach</span>
                </div>

                {project.genre && (
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4" />
                    <span>{project.genre}</span>
                  </div>
                )}
              </div>

              {onViewDetails && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onViewDetails(project.id)}
                  className="hover:bg-neutral-50"
                >
                  View Details
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};